// hooks/api/ticket.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as ticketService from "@/services/ticket.service";
import {
  TicketCreateType,
  TicketParamsType,
  TicketType,
} from "@/services/ticket.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const ticketsKey = {
  all: ["tickets"] as const,
  lists: (filters: TicketParamsType) =>
    [...ticketsKey.all, "lists", filters] as const,
  detail: (id: string) => [...ticketsKey.all, "detail", id] as const,
};

export const useTicketsQuery = (filters: TicketParamsType) =>
  useQuery({
    queryKey: ticketsKey.lists(filters),
    queryFn: () => ticketService.fetchAllTickets(filters),
    placeholderData: (prev) => prev,
  });

export const useTicketByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: ticketsKey.detail(id),
    queryFn: () => ticketService.fetchTicketById(id),
    enabled: enabled && !!id,
  });

export const useCreateTicketMutation = (filters: TicketParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<TicketType, AxiosError, TicketCreateType>({
    mutationFn: (data) => ticketService.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketsKey.lists(filters) });
      notifySuccess("Ticket created successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to create ticket"),
  });
};

export const useUpdateTicketMutation = (filters: TicketParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: TicketCreateType }) =>
      ticketService.updateTicket(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ticketsKey.lists(filters) });
      queryClient.invalidateQueries({
        queryKey: ticketsKey.detail(variables._id),
      });
      notifySuccess("Ticket updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update ticket"),
  });
};

export const useDeleteTicketMutation = (filters: TicketParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => ticketService.deleteTicket(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({ queryKey: ticketsKey.lists(filters) });
      queryClient.removeQueries({ queryKey: ticketsKey.detail(_id) });
      notifySuccess("Ticket deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete ticket"),
  });
};
